import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ChatBubbleIcon, UsersIcon, BookIcon } from "../Dashboard/icons";
import { RobotIcon } from "../Auth/onboarding/icons";
import { getAccountUsage } from "../../actions/account";
import { planLabel } from "../../actions/billing";

// GET /account/usage reports used-vs-limit per resource. The rows are listed
// here for order, labels and colour; every number is the endpoint's.
const ROWS = [
  { key: "assistants", label: "Assistants", variant: "indigo", icon: <RobotIcon /> },
  {
    key: "conversations",
    label: "Conversations",
    variant: "green",
    icon: <ChatBubbleIcon />,
  },
  { key: "leads", label: "Leads", variant: "blue", icon: <UsersIcon /> },
  { key: "knowledge", label: "Knowledge", variant: "amber", icon: <BookIcon /> },
];

const readRows = (usage) =>
  ROWS.map((row) => {
    const entry = usage?.[row.key];
    if (!entry || typeof entry.used !== "number") return null;

    const limit = typeof entry.limit === "number" ? entry.limit : null;
    // -1 is the API's "no cap", which is a count rather than a meter.
    const unlimited = limit === -1;
    const percent =
      unlimited || !limit
        ? 0
        : Math.round((entry.used / limit) * 1000) / 10;

    return {
      ...row,
      used: entry.used,
      limit,
      unlimited,
      percent,
      // Over quota is a real state on this account, so it gets its own
      // treatment rather than a bar that silently stops at full.
      over: !unlimited && limit > 0 && entry.used > limit,
    };
  }).filter(Boolean);

export default function UsageCard() {
  const dispatch = useDispatch();

  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    dispatch(getAccountUsage()).then((result) => {
      if (cancelled) return;

      setLoading(false);

      if (!result?.success) {
        setError(result?.message || "We couldn't load your usage.");
        return;
      }

      setError("");
      setUsage(result.usage);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const rows = readRows(usage);

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="cz-card-title">
          Your usage
          {/* The response names the plan these limits belong to; the period it
              covers isn't reported, so no date range is claimed. */}
          {usage?.plan && (
            <span className="bill-usage-plan">{planLabel(usage.plan)} plan</span>
          )}
        </h2>
      </div>

      {error && <p className="auth-error">{error}</p>}

      {loading ? (
        <div className="bill-usage-grid">
          {ROWS.map((row) => (
            <div className="bill-usage-item" key={row.key}>
              <span className="skel acct-skel--label" />
              <span className="skel bill-usage-skel--value" />
              <span className="skel bill-usage-skel--track" />
            </div>
          ))}
        </div>
      ) : (
        <div className="bill-usage-grid">
          {rows.map((item) => (
            <div className="bill-usage-item" key={item.key}>
              <span className="bill-usage-label">
                <span
                  className={`dash-stat-icon dash-stat-icon--${item.variant} bill-usage-icon`}
                >
                  {item.icon}
                </span>
                {item.label}
              </span>

              <p className={`bill-usage-value${item.over ? " is-over" : ""}`}>
                {item.used.toLocaleString()}{" "}
                <span>
                  {item.unlimited
                    ? "/ Unlimited"
                    : item.limit
                      ? `/ ${item.limit.toLocaleString()}`
                      : ""}
                </span>
              </p>

              {!item.unlimited && Boolean(item.limit) && (
                <>
                  <div className="bill-usage-track">
                    {/* Clamped so an over-quota row fills the track rather
                        than painting past it. */}
                    <span
                      className={`bill-usage-fill bill-usage-fill--${item.over ? "over" : item.variant}`}
                      style={{ width: `${Math.min(100, item.percent)}%` }}
                    />
                  </div>
                  <span
                    className={`bill-usage-percent${item.over ? " is-over" : ""}`}
                  >
                    {item.percent}% used
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && rows.some((row) => row.over) && (
        <p className="bill-usage-warning">
          You are over your plan limit on{" "}
          {rows
            .filter((row) => row.over)
            .map((row) => row.label.toLowerCase())
            .join(" and ")}
          .
        </p>
      )}
    </section>
  );
}
